import { infiniteQueryFetchNextPage } from '@repo/sdk'
import type {
	OrganizationListItem,
	V1BackofficeListOrganizationsData
} from '@repo/sdk/gen'
import { zV1BackofficeListOrganizationsData } from '@repo/sdk/zod'
import { DataTableColumnSettings } from '@repo/ui/components/data-table/column-settings'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/components/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/components/data-table/types'
import { ListInputSearch } from '@repo/ui/components/list-input-search'
import { ListToolbar } from '@repo/ui/components/list-toolbar'
import { PageLayout } from '@repo/ui/components/page-layout'
import { Separator } from '@repo/ui/separator'
import { getErrorCode, raise, zodNumberMaxValue } from '@repo/ui/utils'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
	createFileRoute,
	Link,
	stripSearchParams,
	useNavigate
} from '@tanstack/react-router'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { OperatorBreadcrumb } from '@/routes/-components/operator-breadcrumb'
import { CreateOrganizationAction } from '@/routes/(private)/organizations/-components/create-organization-action'
import {
	OrganizationActiveUsersCell,
	OrganizationCreatedAtCell,
	OrganizationUsageCell
} from '@/routes/(private)/organizations/-components/organization-metric-cells'
import { OrganizationStatusBadge } from '@/routes/(private)/organizations/-components/organization-status-badge'
import { OrganizationsEmpty } from '@/routes/(private)/organizations/-components/organizations-empty'
import { OrganizationsFilters } from '@/routes/(private)/organizations/-components/organizations-filters'
import {
	ORGANIZATION_STATUS_READINGS,
	statusesForReadings
} from '@/routes/(private)/organizations/-lib/organization-status'
import { organizationsListQueryOptions } from '@/routes/(private)/organizations/-query/query-options'
import { usePageSize } from '@/stores/use-page-size'

/**
 * The two orders the endpoint can produce, and the two directions. Literal, but
 * checked against the contract: a column the API stops accepting — or starts —
 * is a compile error here rather than a `400` the operator meets by clicking a
 * header.
 *
 * Declared above the schema that consumes them rather than with the file's
 * other constants, because a `const` read at module load cannot be hoisted.
 */
type SortBy = NonNullable<
	NonNullable<V1BackofficeListOrganizationsData['query']>['sortBy']
>

const SORT_BY_VALUES = [
	'createdAt',
	'usageBytes'
] as const satisfies readonly SortBy[]

const SORT_DIRECTION_VALUES = [
	'asc',
	'desc'
] as const satisfies readonly NonNullable<
	NonNullable<V1BackofficeListOrganizationsData['query']>['sortDirection']
>[]

const searchSchema = z.object({
	status: z.array(z.enum(ORGANIZATION_STATUS_READINGS)).optional(),
	search: z.string().trim().default(''),
	by: z.enum(SORT_BY_VALUES).default('createdAt'),
	direction: z.enum(SORT_DIRECTION_VALUES).default('desc')
})

export const Route = createFileRoute('/(private)/organizations/')({
	component: OperatorOrganizations,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	}
})

/**
 * The parc of customers: how many there are, what state each is in, how many
 * people can sign in to it, how much it holds and since when that figure has
 * stood.
 *
 * **Five columns and no more.** No slug, and above all no storage identifier:
 * the usage is a number, never a bucket, a project or a prefix.
 * `OrganizationListItem` carries none of those fields, so the constraint is the
 * contract's rather than this file's discipline — and the table cannot leak
 * what it is never handed. The id is not a column either; it is the address of
 * the row's own page, which the name links to.
 *
 * **The status column says what, and the page behind the name says why.** The
 * badge used to hover a tooltip fed by a second `get-organizations` call for
 * every failed row on screen — one extra request in exactly the condition an
 * operator opens this dashboard for. OPSSTD-5427 offered two ways out; this is
 * the one that needs no contract change, and it is also the better reading:
 * `failedStep`, `failureCode`, the external system's verbatim words, the
 * attempt counter and the retry are five things, and a tooltip had room for
 * three of them at most.
 *
 * Paging is the endpoint's cursor, consumed as an infinite query: the API hands
 * out `nextCursor` and no way to address a page by number, so the table loads
 * forward as the operator scrolls rather than pretending to numbered pages it
 * could not honour. A cursor the server rejects — expired, or invalidated by an
 * order that changed underneath — is answered by restarting from the first page
 * in silence, which is what `cursor_invalid` asks for; showing an error there
 * would report a routine event as a fault.
 *
 * Sorting and filtering never carry a cursor across: they are part of the query
 * key, so changing either starts the listing again from its first page by
 * construction rather than by remembering to reset something.
 */
function OperatorOrganizations() {
	const { t } = useTranslation()
	const navigate = useNavigate({ from: Route.fullPath })
	const params = Route.useSearch()
	const queryClient = useQueryClient()
	const pageSize = usePageSize((state) => state.pageSize)

	const listQuery = {
		limit: Math.min(pageSize, MAX_PAGE_SIZE),
		name: params.search || undefined,
		status: params.status && statusesForReadings(params.status),
		sortBy: params.by,
		sortDirection: params.direction
	} satisfies Omit<
		NonNullable<V1BackofficeListOrganizationsData['query']>,
		'cursor'
	>

	const organizations = useInfiniteQuery(
		organizationsListQueryOptions(listQuery)
	)
	const rows = organizations.data?.organizations ?? EMPTY_ORGANIZATIONS

	// Not an error to show. The cursor expired or the order moved under it, and
	// the answer is another first page — which is what the load-more handler
	// asks the cache for.
	const cursorExpired = getErrorCode(organizations.error) === 'cursor_invalid'

	const columns: DataTableColumn<OrganizationListItem, SortBy>[] = [
		{
			id: 'name',
			header: t('operator-organizations:columns.name'),
			pinned: 'left',
			cell: (organization) => (
				<Link
					className='truncate font-medium hover:underline'
					params={{ organizationId: organization.id }}
					to='/organizations/$organizationId'
				>
					{organization.name}
				</Link>
			),
			minWidth: 220,
			flex: 2
		},
		{
			id: 'status',
			header: t('operator-organizations:columns.status'),
			cell: (organization) => (
				<OrganizationStatusBadge status={organization.status} />
			),
			minWidth: 190,
			flex: 1
		},
		{
			id: 'activeUserCount',
			header: t('operator-organizations:columns.active_users'),
			cell: (organization) => (
				<OrganizationActiveUsersCell organization={organization} />
			),
			minWidth: 140,
			flex: 1
		},
		{
			id: 'usage',
			header: t('operator-organizations:columns.usage'),
			sortKey: 'usageBytes',
			cell: (organization) => (
				<OrganizationUsageCell
					limit={organization.quotaBytes}
					organization={organization}
				/>
			),
			minWidth: 230,
			flex: 1
		},
		{
			id: 'createdAt',
			header: t('operator-organizations:columns.created_at'),
			sortKey: 'createdAt',
			cell: (organization) => (
				<OrganizationCreatedAtCell organization={organization} />
			),
			minWidth: 170,
			flex: 1
		}
	]

	const columnsState = useDataTableColumnState(columns)

	/**
	 * One more page, and the one refusal that is not a failure.
	 *
	 * `cursor_invalid` says the cursor no longer addresses anything — it
	 * expired, or the order it was minted under has moved. The answer is to
	 * start the listing again from its first page, silently: resetting the
	 * query drops the pages held and refetches with no cursor at all. An error
	 * toast here would report a routine event as a fault.
	 */
	async function loadNextPage() {
		const result = await infiniteQueryFetchNextPage(organizations)
		if (getErrorCode(result?.error) === 'cursor_invalid') {
			await queryClient.resetQueries({
				queryKey: makeQueryKeys.organizations.index(listQuery)
			})
		}
	}

	return (
		<PageLayout
			bar={
				<ListToolbar
					actions={<CreateOrganizationAction />}
					search={
						<ListInputSearch
							placeholder={t(
								'operator-organizations:search_placeholder'
							)}
						/>
					}
				>
					<OrganizationsFilters />
				</ListToolbar>
			}
			breadcrumb={<OperatorBreadcrumb />}
		>
			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6'>
				<DataTable
					columns={columns}
					columnsState={columnsState}
					data={rows}
					footer={
						<>
							<DataTableColumnSettings
								columns={columns}
								columnsState={columnsState}
							/>
							<Separator
								className='my-3'
								orientation='vertical'
							/>
							<span>
								{t('operator-organizations:total', {
									count: organizations.data?.total ?? 0
								})}
							</span>
						</>
					}
					loadMore={{
						hasMore: organizations.hasNextPage,
						loading: organizations.isFetchingNextPage,
						onLoadMore: loadNextPage
					}}
					rowHeight={56}
					sort={{
						state: {
							by: params.by,
							direction: params.direction
						},
						// A different order invalidates any cursor already
						// handed out, so the key has to change with it —
						// which it does, because the sort is part of the
						// listing's identity rather than something layered
						// on top of it.
						onSortChange: (next) =>
							navigate({
								search: (prev) => ({
									...prev,
									by: next.by,
									direction: next.direction
								}),
								replace: true
							})
					}}
					state={{
						empty: (
							<OrganizationsEmpty
								filtered={
									params.search !== '' ||
									params.status !== undefined
								}
							/>
						),
						error: organizations.isError && !cursorExpired,
						loading:
							organizations.isPending ||
							organizations.isPlaceholderData
					}}
				/>
			</div>
		</PageLayout>
	)
}

const EMPTY_ORGANIZATIONS: OrganizationListItem[] = []

/**
 * The endpoint's own ceiling on a page, off the contract rather than retyped.
 * The rows-per-page preference is app-wide and its options are not bound to
 * this route's endpoint, so the two are reconciled here instead of by hoping
 * they stay in step.
 *
 * `raise` rather than a fallback: a schema that has lost its ceiling means the
 * contract changed shape, and a quietly substituted number would let this list
 * ask for pages the server refuses with a `400`.
 */
const MAX_PAGE_SIZE =
	zodNumberMaxValue(
		zV1BackofficeListOrganizationsData.shape.query.unwrap().shape.limit
	) ?? raise('list-organizations lost its page-size ceiling')
